/** @jsxImportSource react */
import { useState } from 'react'
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined'
import {
  Alert, Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Select,
  Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Chip, Typography,
} from '@mui/material'
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { invitesAPI, membersAPI, rolesAPI } from '../../api/crm'
import { selectIsTenantAdmin } from '../../store/slices/tenantSlice'
import { useFeatures, useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { formatDate, formatRelativeTime } from '../../utils/helpers'
import { useNotify } from '../../components/common/NotificationProvider'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import InviteLinkDialog from '../../components/common/InviteLinkDialog'

/** Invites: admins, or members who join with the default role (change it after they join). */
const ROLES = [
  { value: 'TENANT_ADMIN', label: 'Admin' },
  { value: 'MEMBER', label: 'Member (default role)' },
]
const ADMIN = 'ADMIN'
/** One picker for both: Admin (full access, manages the workspace) or a custom role for members. */
const roleValue = (m) => (m.role === 'TENANT_ADMIN' ? ADMIN : m.customRoleId || '')
const roleLabel = (m) => (m.role === 'TENANT_ADMIN' ? 'Admin' : m.customRoleName || 'Member')

const MembersPage = () => {
  const tenantId = useTenantKey()
  const tenantName = useSelector((state) => state.tenant.current?.name)
  const myEmail = useSelector((state) => state.auth.user?.email?.toLowerCase())
  const isAdmin = useSelector(selectIsTenantAdmin)
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [link, setLink] = useState(null)

  const queryKey = [tenantId, 'members']
  const { data: members, isLoading, error } = useQuery({ queryKey, queryFn: membersAPI.list })
  const { data: roles = [] } = useQuery({ queryKey: [tenantId, 'roles'], queryFn: rolesAPI.list, enabled: isAdmin })

  const onChanged = (message) => {
    notify(message)
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: ['me'] })
    queryClient.invalidateQueries({ queryKey: [tenantId, 'roles'] })
  }

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => membersAPI.update(id, body),
    onSuccess: (m) => onChanged(`Updated ${m.email}`),
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const { emailEnabled } = useFeatures()
  const resetMutation = useMutation({
    mutationFn: (member) => membersAPI.sendPasswordReset(member.id),
    onSuccess: (_, member) => notify(`Password reset link emailed to ${member.email}`),
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const removeMutation = useMutation({
    mutationFn: (member) => membersAPI.remove(member.id),
    onSuccess: (_, member) => {
      setRemoving(null)
      onChanged(`Removed ${member.email}`)
    },
    onError: (e) => {
      setRemoving(null)
      notify(getErrorMessage(e), 'error')
    },
  })

  if (isLoading) return <Loading />
  if (error) return <Alert severity="error">{getErrorMessage(error)}</Alert>

  return (
    <>
      <PageHeader
        title="Members"
        subtitle={`People with access to ${tenantName}. Admins manage members and forms; "Reports to" lets team-scoped roles (managers) see their team's records.`}
        actions={isAdmin && <Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setInviteOpen(true)}>Invite member</Button>}
      />
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Member</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Reports to</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Added</TableCell>
              {isAdmin && <TableCell align="right" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar src={m.pictureUrl || undefined} imgProps={{ referrerPolicy: 'no-referrer' }} alt=""
                      sx={{ width: 32, height: 32, fontSize: 14 }}>{(m.name || m.email)[0].toUpperCase()}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} component="span">{m.name || m.email}</Typography>
                      {m.email === myEmail && <Chip label="you" variant="outlined" sx={{ ml: 1 }} />}
                      {m.name && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{m.email}</Typography>}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  {isAdmin && roles.length > 0 ? (
                    <Select value={roleValue(m)} variant="standard" disableUnderline sx={{ fontSize: 'inherit' }} inputProps={{ 'aria-label': `Role of ${m.email}` }}
                      onChange={(e) => updateMutation.mutate({
                        id: m.id,
                        body: e.target.value === ADMIN ? { role: 'TENANT_ADMIN' } : { role: 'MEMBER', customRoleId: e.target.value },
                      })}>
                      <MenuItem value={ADMIN}>Admin</MenuItem>
                      {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}{r.isDefault ? ' (default)' : ''}</MenuItem>)}
                    </Select>
                  ) : roleLabel(m)}
                </TableCell>
                <TableCell>
                  {isAdmin ? (
                    <Select value={m.managerUserId || ''} displayEmpty variant="standard" disableUnderline sx={{ fontSize: 'inherit', minWidth: 110 }}
                      inputProps={{ 'aria-label': `${m.email} reports to` }}
                      onChange={(e) => updateMutation.mutate({
                        id: m.id,
                        body: e.target.value ? { managerUserId: e.target.value } : { clearManager: true },
                      })}>
                      <MenuItem value=""><Typography variant="body2" color="text.secondary">Nobody</Typography></MenuItem>
                      {members.filter((x) => x.userId && x.active && x.id !== m.id).map((x) => (
                        <MenuItem key={x.id} value={x.userId}>{x.name || x.email}</MenuItem>
                      ))}
                    </Select>
                  ) : (members.find((x) => x.userId && x.userId === m.managerUserId)?.name
                    || members.find((x) => x.userId && x.userId === m.managerUserId)?.email || '—')}
                </TableCell>
                <TableCell>
                  <Switch size="small" checked={m.active} disabled={!isAdmin}
                    onChange={(e) => updateMutation.mutate({ id: m.id, body: { active: e.target.checked } })}
                    inputProps={{ 'aria-label': `Active: ${m.email}` }} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(m.createdOn)}</TableCell>
                {isAdmin && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {emailEnabled && m.userId && m.active && (
                      <Tooltip title="Email them a link to set a new password (you won't see the link)">
                        <IconButton onClick={() => resetMutation.mutate(m)} disabled={resetMutation.isPending} aria-label={`Send password reset to ${m.email}`}>
                          <LockResetOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove">
                      <IconButton onClick={() => setRemoving(m)} aria-label={`Remove ${m.email}`}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {isAdmin && <PendingInvites onLink={setLink} />}

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onInvited={setLink} />
      <InviteLinkDialog link={link} onClose={() => setLink(null)} />
      <ConfirmDialog
        open={!!removing}
        title="Remove member?"
        message={`${removing?.email} will lose access to ${tenantName}. You can add them again later.`}
        confirmLabel="Remove"
        destructive
        busy={removeMutation.isPending}
        onConfirm={() => removeMutation.mutate(removing)}
        onClose={() => setRemoving(null)}
      />
    </>
  )
}

/**
 * Admin view of invites not yet accepted, with whether each link was emailed. Links can't be shown
 * again, only re-issued (Resend), which emails the new one when delivery is on.
 */
const PendingInvites = ({ onLink }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [revoking, setRevoking] = useState(null)
  const queryKey = [tenantId, 'invites']
  const { data: invites = [] } = useQuery({ queryKey, queryFn: invitesAPI.list })

  const regenerate = useMutation({
    mutationFn: (invite) => invitesAPI.regenerate(invite.id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey })
      onLink(result)
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const revoke = useMutation({
    mutationFn: (invite) => invitesAPI.revoke(invite.id),
    onSuccess: (_, invite) => {
      setRevoking(null)
      notify(`Invite for ${invite.email} revoked`)
      queryClient.invalidateQueries({ queryKey })
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  if (invites.length === 0) return null
  return (
    <>
      <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>Pending invites</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Invited by</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {invites.map((invite) => (
              <TableRow key={invite.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{invite.name || invite.email}</Typography>
                  {invite.name && <Typography variant="caption" color="text.secondary">{invite.email}</Typography>}
                </TableCell>
                <TableCell>{ROLES.find((r) => r.value === invite.role)?.label}</TableCell>
                <TableCell>
                  {invite.expired ? <Chip label="expired" color="warning" variant="outlined" /> : formatRelativeTime(invite.expiresAt)}
                </TableCell>
                <TableCell>
                  {invite.emailedAt ? (
                    <Tooltip title={formatDate(invite.emailedAt)}>
                      <Typography variant="body2" component="span">Emailed {formatRelativeTime(invite.emailedAt)}</Typography>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Email is off or the last attempt failed: the link was only shown to the admin who created it">
                      <Typography variant="body2" component="span" color="text.secondary">Not emailed</Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{invite.invitedBy}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Resend: a new link, emailed when email is set up (the old link stops working)">
                    <IconButton onClick={() => regenerate.mutate(invite)} disabled={regenerate.isPending} aria-label={`Resend invite to ${invite.email}`}>
                      <ForwardToInboxOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Revoke">
                    <IconButton onClick={() => setRevoking(invite)} aria-label={`Revoke invite for ${invite.email}`}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDialog
        open={!!revoking}
        title="Revoke invite?"
        message={`The link sent to ${revoking?.email} will stop working.`}
        confirmLabel="Revoke"
        destructive
        busy={revoke.isPending}
        onConfirm={() => revoke.mutate(revoking)}
        onClose={() => setRevoking(null)}
      />
    </>
  )
}

const InviteDialog = ({ open, onClose, onInvited }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('MEMBER')
  const mutation = useMutation({
    mutationFn: invitesAPI.create,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'invites'] })
      setEmail('')
      setName('')
      setRole('MEMBER')
      onClose()
      onInvited(result)
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); mutation.mutate({ email: email.trim(), role, name: name.trim() || undefined }) } }}>
      <DialogTitle>Invite member</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {mutation.error && <Alert severity="error">{getErrorMessage(mutation.error)}</Alert>}
          <TextField label="Email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} helperText="You’ll get a link to share. They join by opening it." />
          <TextField label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 100 }}
            helperText="Shown to the team until they set their own." />
          <TextField select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={mutation.isPending}>Create invite</Button>
      </DialogActions>
    </Dialog>
  )
}

export default MembersPage
